import React from 'react';

/**
 * Safety net for DOM-level render errors such as
 * "Failed to execute 'removeChild' on 'Node': The node to be removed is not a
 * child of this node."
 *
 * Those errors come from outside the app — a browser translating the page or an
 * extension rewriting nodes — which leaves React and the DOM out of sync. React
 * would otherwise unmount the whole app and leave a blank page. Here the tree is
 * therefore remounted once, silently, and only if the error repeats does the
 * visitor get a reload option instead of an empty screen.
 */
export default class AppErrorBoundary extends React.Component {
  state = { error: null, attempt: 0 };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error('App render error:', error);
    if (this.state.attempt === 0) {
      // A fresh mount clears a desynced DOM in almost every case.
      this.setState({ error: null, attempt: 1 });
    }
  }

  render() {
    const { error, attempt } = this.state;

    if (error) {
      // First failure: the remount above is already on its way.
      if (attempt === 0) return null;

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md text-center">
            <p className="font-heading text-xl font-bold text-gray-900 mb-2">
              Etwas ist schiefgelaufen
            </p>
            <p className="text-gray-500 mb-6">
              Something went wrong. Bitte lade die Seite neu.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors"
            >
              Neu laden
            </button>
          </div>
        </div>
      );
    }

    return <React.Fragment key={attempt}>{this.props.children}</React.Fragment>;
  }
}